import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent } from '../components/ui/collapsible';
import api from '../lib/api';
import { usePersistentFilters } from '../lib/persistentFilters';
import { toast } from 'sonner';
import { Search, FileText, Plus, Trash2, Loader2, AlertTriangle, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { formatNumber, formatDate } from '../lib/utils';
import { GreyBillForm } from '../components/forms';
import { usePermissions, PermissionGuard } from '../contexts/PermissionsContext';
import { useCompany } from '../contexts/CompanyContext';

const GreyBillsList = () => {
  const navigate = useNavigate();
  const { hasModulePermission } = usePermissions();
  const { activeCompany } = useCompany();
  const [searchParams] = useSearchParams();
  const preSelectedOrderId = searchParams.get('orderId');
  
  const [bills, setBills] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [subqualities, setSubqualities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = usePersistentFilters('greyBills.search', '');
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = usePersistentFilters('greyBills.filters', {
    purchase_bill_number: '',
    supplier_id: '',
    quality_id: '',
    subquality_id: '',
    bill_date_from: '',
    bill_date_to: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeCompany?.company_id]);

  useEffect(() => {
    if (preSelectedOrderId) {
      setShowAddForm(true);
    }
  }, [preSelectedOrderId]);

  const fetchData = async () => {
    try {
      const [billsRes, ordersRes, suppliersRes, qualitiesRes, subqualitiesRes] = await Promise.allSettled([
        api.get('/grey-bills'),
        api.get('/grey-orders'),
        api.get('/suppliers'),
        api.get('/qualities'),
        api.get('/subqualities')
      ]);
      setBills(billsRes.status === 'fulfilled' ? billsRes.value.data : []);
      setOrders(ordersRes.status === 'fulfilled' ? ordersRes.value.data : []);
      setSuppliers(suppliersRes.status === 'fulfilled' ? suppliersRes.value.data : []);
      setQualities(qualitiesRes.status === 'fulfilled' ? qualitiesRes.value.data : []);
      setSubqualities(subqualitiesRes.status === 'fulfilled' ? subqualitiesRes.value.data : []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load bills');
      setLoading(false);
    }
  };

  const getOrderDetails = (orderId) => {
    return orders.find(o => o.order_id === orderId) || {};
  };

  // Apply filters
  const filteredBills = bills.filter(bill => {
    const order = getOrderDetails(bill.order_id);
    
    // Search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        bill.bill_number?.toLowerCase().includes(searchLower) ||
        bill.purchase_bill_number?.toLowerCase().includes(searchLower) ||
        bill.party_name?.toLowerCase().includes(searchLower) ||
        order.order_number?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Advanced filters
    if (filters.purchase_bill_number && !bill.purchase_bill_number?.toLowerCase().includes(filters.purchase_bill_number.toLowerCase())) return false;
    if (filters.supplier_id && order.supplier_id !== filters.supplier_id) return false;
    if (filters.quality_id && order.quality_id !== filters.quality_id) return false;
    if (filters.subquality_id && order.subquality_id !== filters.subquality_id) return false;
    
    // Date range filters
    if (filters.bill_date_from) {
      const billDate = new Date(bill.billed_date);
      const fromDate = new Date(filters.bill_date_from);
      if (billDate < fromDate) return false;
    }
    if (filters.bill_date_to) {
      const billDate = new Date(bill.billed_date);
      const toDate = new Date(filters.bill_date_to);
      if (billDate > toDate) return false;
    }
    
    return true;
  });

  const handleAddSuccess = () => {
    setShowAddForm(false);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/grey-bills/${deleteTarget.bill_id}`);
      toast.success('Bill deleted successfully!');
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bill');
    }
    setDeleting(false);
  };

  const resetFilters = () => {
    setFilters({
      purchase_bill_number: '',
      supplier_id: '',
      quality_id: '',
      subquality_id: '',
      bill_date_from: '',
      bill_date_to: ''
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="grey-bills-list" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Grey Bills
            </h1>
            <p className="text-slate-600 mt-1">{filteredBills.length} bills</p>
          </div>
          
          <PermissionGuard module="grey_bills" permission="create">
          <Sheet open={showAddForm} onOpenChange={setShowAddForm}>
            <SheetTrigger asChild>
              <Button data-testid="add-bill-btn" className="bg-slate-900 hover:bg-slate-800">
                <Plus className="h-4 w-4 mr-2" />
                Add Grey Bill
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-md overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Add Grey Bill</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <GreyBillForm 
                  orderId={preSelectedOrderId}
                  onSuccess={handleAddSuccess}
                  onCancel={() => setShowAddForm(false)}
                />
              </div>
            </SheetContent>
          </Sheet>
          </PermissionGuard>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="search-bills-input"
              placeholder="Search by bill number, party name, order..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="h-11"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge className="ml-2 bg-slate-900 text-white">{activeFilterCount}</Badge>
            )}
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </Button>
        </div>

        {/* Advanced Filters Panel */}
        <Collapsible open={showFilters} onOpenChange={setShowFilters}>
          <CollapsibleContent>
            <Card className="mb-4 border border-slate-200">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs">Purchase Bill Number</Label>
                    <Input
                      placeholder="Search bill #..."
                      value={filters.purchase_bill_number}
                      onChange={(e) => setFilters({...filters, purchase_bill_number: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Supplier</Label>
                    <Select value={filters.supplier_id} onValueChange={(v) => setFilters({...filters, supplier_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All suppliers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All suppliers</SelectItem>
                        {suppliers.map(s => (
                          <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Quality</Label>
                    <Select value={filters.quality_id} onValueChange={(v) => setFilters({...filters, quality_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All qualities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All qualities</SelectItem>
                        {qualities.map(q => (
                          <SelectItem key={q.quality_id} value={q.quality_id}>{q.quality_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Sub Quality</Label>
                    <Select value={filters.subquality_id} onValueChange={(v) => setFilters({...filters, subquality_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All sub qualities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All sub qualities</SelectItem>
                        {subqualities.map(sq => (
                          <SelectItem key={sq.subquality_id} value={sq.subquality_id}>{sq.subquality_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Bill Date From</Label>
                    <Input
                      type="date"
                      value={filters.bill_date_from}
                      onChange={(e) => setFilters({...filters, bill_date_from: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Bill Date To</Label>
                    <Input
                      type="date"
                      value={filters.bill_date_to}
                      onChange={(e) => setFilters({...filters, bill_date_to: e.target.value})}
                      className="h-9"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end mt-4 gap-2">
                  <Button variant="outline" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Active Filter Tags */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {filters.supplier_id && (
              <Badge variant="secondary" className="gap-1">
                Supplier: {suppliers.find(s => s.supplier_id === filters.supplier_id)?.name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, supplier_id: ''})} />
              </Badge>
            )}
            {filters.quality_id && (
              <Badge variant="secondary" className="gap-1">
                Quality: {qualities.find(q => q.quality_id === filters.quality_id)?.quality_name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, quality_id: ''})} />
              </Badge>
            )}
            {filters.subquality_id && (
              <Badge variant="secondary" className="gap-1">
                Sub Quality: {subqualities.find(sq => sq.subquality_id === filters.subquality_id)?.subquality_name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, subquality_id: ''})} />
              </Badge>
            )}
            {filters.bill_date_from && (
              <Badge variant="secondary" className="gap-1">
                From: {filters.bill_date_from}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, bill_date_from: ''})} />
              </Badge>
            )}
            {filters.bill_date_to && (
              <Badge variant="secondary" className="gap-1">
                To: {filters.bill_date_to}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, bill_date_to: ''})} />
              </Badge>
            )}
            {filters.purchase_bill_number && (
              <Badge variant="secondary" className="gap-1">
                Bill #: {filters.purchase_bill_number}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, purchase_bill_number: ''})} />
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Bills Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bill #</TableHead>
                  <TableHead>Order #</TableHead>
                  <TableHead>Purchase Bill</TableHead>
                  <TableHead>Party Name</TableHead>
                  <TableHead>Bill Date</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Bill Rate</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBills.length > 0 ? (
                  filteredBills.map((bill) => {
                    const order = getOrderDetails(bill.order_id);
                    return (
                      <TableRow 
                        key={bill.bill_id} 
                        data-testid={`bill-row-${bill.bill_number}`}
                        className={`cursor-pointer hover:bg-slate-50 ${bill.rate_mismatch ? 'bg-amber-50/50' : ''}`}
                        onClick={() => navigate(`/grey-bills/${bill.bill_id}`)}
                      >
                        <TableCell className="font-mono font-medium">
                          <div className="flex items-center gap-2">
                            {bill.bill_number}
                            {bill.rate_mismatch && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Rate Mismatch
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className="cursor-pointer hover:bg-slate-100"
                            onClick={(e) => { e.stopPropagation(); navigate(`/grey-orders/${bill.order_id}`); }}
                          >
                            {order.order_number || bill.order_id}
                          </Badge>
                        </TableCell>
                        <TableCell>{bill.purchase_bill_number}</TableCell>
                        <TableCell className="font-medium">{bill.party_name}</TableCell>
                        <TableCell>{formatDate(bill.billed_date)}</TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{formatNumber(bill.billed_quantity)}</span>
                          <span className="text-slate-500 ml-1">{order.order_unit}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {bill.bill_rate ? (
                            <span className={`font-medium ${bill.rate_mismatch ? 'text-amber-700' : ''}`}>
                              ₹{formatNumber(bill.bill_rate)}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{bill.created_by_name}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {hasModulePermission('grey_bills', 'delete') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteTarget(bill)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      No bills found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Bill</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete bill <strong>{deleteTarget?.bill_number}</strong>? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GreyBillsList;
